import { ClientMouAcceptRequest, ClientMouRejectRequest, HTTPHeaders, MOUSignatureApi } from 'eve-account-client';
import { useMemo } from 'react';
import { MutationConfig, MutationResultPair, QueryConfig, QueryResult, useMutation, useQuery } from 'react-query';
import { useConfiguration } from './BaseApi';

const useMOUSignatureApi = (headers: HTTPHeaders | undefined = undefined): MOUSignatureApi => {
  const configuration = useConfiguration(headers);
  const api = useMemo(() => new MOUSignatureApi(configuration), [configuration]);
  return api;
};

const getUnsignedMouDocument = (authCode: string, api: MOUSignatureApi): Promise<Blob> => {
  const request: Promise<Blob> = api.getUnsignedClientMouDocument({ authCode });
  return request;
};

export const useUnsignedMouDocumentQuery = (
  authCode: string,
  queryOptions?: QueryConfig<Blob>,
): QueryResult<Blob, Error | Response> => {
  const api = useMOUSignatureApi({ Accept: 'application/pdf,application/json' });
  return useQuery<Blob, Error | Response>(['mou', authCode], () => getUnsignedMouDocument(authCode, api), {
    ...queryOptions,
    enabled: Boolean(authCode) && queryOptions?.enabled !== false,
  });
};

const getSignedMouDocument = (authCode: string, api: MOUSignatureApi): Promise<Blob> => {
  const request: Promise<Blob> = api.getSignedClientMouDocument({ authCode });
  return request;
};

export const useSignedMouDocumentQuery = (
  authCode: string,
  queryOptions?: QueryConfig<Blob>,
): QueryResult<Blob, Error | Response> => {
  const api = useMOUSignatureApi({ Accept: 'application/pdf,application/json' });
  return useQuery<Blob, Error | Response>(['mou', authCode], () => getSignedMouDocument(authCode, api), {
    ...queryOptions,
    enabled: Boolean(authCode) && queryOptions?.enabled !== false,
  });
};

export const useAcceptClientMou = ({
  config,
}: {
  config?: MutationConfig<
    void,
    Error,
    {
      clientMouAcceptRequest: ClientMouAcceptRequest;
    },
    unknown
  >;
}): MutationResultPair<void, Error, { clientMouAcceptRequest: ClientMouAcceptRequest }, unknown> => {
  const api = useMOUSignatureApi();

  return useMutation(
    ({ clientMouAcceptRequest }) => {
      return api.acceptClientMou({ clientMouAcceptRequest });
    },
    {
      useErrorBoundary: false,
      ...config,
    },
  );
};

export const useRejectClientMou = ({
  config,
}: {
  config?: MutationConfig<
    void,
    Error,
    {
      clientMouRejectRequest: ClientMouRejectRequest;
    },
    unknown
  >;
}): MutationResultPair<void, Error, { clientMouRejectRequest: ClientMouRejectRequest }, unknown> => {
  const api = useMOUSignatureApi();

  return useMutation(
    ({ clientMouRejectRequest }) => {
      return api.rejectClientMou({ clientMouRejectRequest });
    },
    {
      useErrorBoundary: false,
      ...config,
    },
  );
};
